// Context CMP — gestione consenso cookie (GDPR) lato pubblico.
// - Legge la config dal backend (testi, categorie, script per categoria).
// - Persistenza scelta in localStorage + registrazione server-side (registro consensi).
// - Blocco reale degli script: gli script marketing/analytics vengono iniettati
//   SOLO dopo il consenso della relativa categoria (idempotente).
// - Riapertura preferenze via evento globale 'open-cookie-preferences' (footer).
import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { cookieService } from '@/services/cookie.service';

const STORAGE_KEY = 'sila_cookie_consent_v1';
const CookieConsentContext = createContext(null);

function uuid() {
  try {
    if (typeof crypto !== 'undefined' && crypto.randomUUID) return crypto.randomUUID();
  } catch { /* noop */ }
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
    const r = (Math.random() * 16) | 0;
    return (c === 'x' ? r : (r & 0x3) | 0x8).toString(16);
  });
}

function readStored() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch { return null; }
}

// Inietta uno <script> in modo idempotente (dedupe per data-cmp id).
function injectScript(key, attrs, inlineCode) {
  if (typeof document === 'undefined') return;
  if (document.querySelector(`[data-cmp="${key}"]`)) return;
  const s = document.createElement('script');
  s.setAttribute('data-cmp', key);
  Object.entries(attrs || {}).forEach(([k, v]) => s.setAttribute(k, v));
  if (inlineCode) s.text = inlineCode;
  document.head.appendChild(s);
}

// Applica gli script consentiti in base alle categorie accettate.
function applyConsentedScripts(config, categories) {
  if (!config || !categories) return;
  (config.categories || []).forEach((cat) => {
    if (cat.required) return; // i "necessari" non sono script gestiti
    if (!categories[cat.key]) return; // categoria non consentita -> nessun caricamento
    (cat.scripts || []).forEach((sc, i) => {
      if (!sc.enabled || !sc.id) return; // niente id/spento -> skip (predisposto ma inattivo)
      const key = `${cat.key}-${sc.provider || 'custom'}-${i}`;
      const provider = (sc.provider || '').toLowerCase();
      if (provider === 'ga4' || provider === 'gtag') {
        injectScript(key, { src: `https://www.googletagmanager.com/gtag/js?id=${sc.id}`, async: 'true' });
        injectScript(`${key}-init`, {}, `window.dataLayer=window.dataLayer||[];function gtag(){dataLayer.push(arguments);}gtag('js',new Date());gtag('config','${sc.id}');`);
      } else if (provider === 'meta' || provider === 'facebook' || provider === 'pixel') {
        injectScript(key, {}, `!function(f,b,e,v,n,t,s){if(f.fbq)return;n=f.fbq=function(){n.callMethod?n.callMethod.apply(n,arguments):n.queue.push(arguments)};if(!f._fbq)f._fbq=n;n.push=n;n.loaded=!0;n.version='2.0';n.queue=[];t=b.createElement(e);t.async=!0;t.src=v;s=b.getElementsByTagName(e)[0];s.parentNode.insertBefore(t,s)}(window,document,'script','https://connect.facebook.net/en_US/fbevents.js');fbq('init','${sc.id}');fbq('track','PageView');`);
      } else if (sc.id.startsWith('http')) {
        injectScript(key, { src: sc.id, async: 'true' });
      }
    });
  });
}

export function CookieConsentProvider({ children }) {
  const [config, setConfig] = useState(null);
  const [stored, setStored] = useState(() => readStored());
  const [bannerOpen, setBannerOpen] = useState(false);
  const [prefsOpen, setPrefsOpen] = useState(false);

  // Carica la config pubblica una volta.
  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const cfg = await cookieService.publicConfig();
        if (!alive) return;
        setConfig(cfg);
        const s = readStored();
        const needsConsent = cfg.enabled && (!s || s.policy_version !== cfg.policy_version);
        setBannerOpen(!!needsConsent);
        if (s && s.categories && cfg.enabled) applyConsentedScripts(cfg, s.categories);
      } catch {
        // se la config non carica, non mostrare nulla (fail-safe silenzioso)
      }
    })();
    return () => { alive = false; };
  }, []);

  // Riapertura preferenze da footer / altrove.
  useEffect(() => {
    const open = () => setPrefsOpen(true);
    window.addEventListener('open-cookie-preferences', open);
    return () => window.removeEventListener('open-cookie-preferences', open);
  }, []);

  const persist = useCallback(async (categories, action) => {
    const consent_id = (stored && stored.consent_id) || uuid();
    const record = {
      consent_id,
      categories,
      policy_version: config?.policy_version || '1.0',
      ts: new Date().toISOString(),
    };
    try { localStorage.setItem(STORAGE_KEY, JSON.stringify(record)); } catch { /* noop */ }
    setStored(record);
    setBannerOpen(false);
    setPrefsOpen(false);
    if (config) applyConsentedScripts(config, categories);
    // registrazione server-side (best-effort, non blocca la UI)
    try {
      await cookieService.submitConsent({
        action,
        categories,
        consent_id,
        policy_version: config?.policy_version || '1.0',
        path: typeof window !== 'undefined' ? window.location.pathname : '',
      });
    } catch { /* best-effort */ }
  }, [config, stored]);

  const allCats = useMemo(() => {
    const base = { necessary: true };
    (config?.categories || []).forEach((c) => { if (!c.required) base[c.key] = false; });
    return base;
  }, [config]);

  const acceptAll = useCallback(() => {
    const cats = { ...allCats };
    Object.keys(cats).forEach((k) => { cats[k] = true; });
    return persist(cats, 'accept_all');
  }, [allCats, persist]);

  const rejectAll = useCallback(() => {
    const cats = { ...allCats, necessary: true };
    return persist(cats, 'reject_all');
  }, [allCats, persist]);

  const savePreferences = useCallback((cats) => {
    return persist({ ...cats, necessary: true }, 'save');
  }, [persist]);

  const hasConsent = useCallback((key) => {
    if (key === 'necessary') return true;
    return !!(stored && stored.categories && stored.categories[key]);
  }, [stored]);

  const value = {
    config,
    stored,
    bannerOpen,
    prefsOpen,
    openPreferences: () => setPrefsOpen(true),
    closePreferences: () => setPrefsOpen(false),
    acceptAll,
    rejectAll,
    savePreferences,
    hasConsent,
  };

  return (
    <CookieConsentContext.Provider value={value}>
      {children}
    </CookieConsentContext.Provider>
  );
}

export function useCookieConsent() {
  const ctx = useContext(CookieConsentContext);
  if (!ctx) return {
    config: null, stored: null, bannerOpen: false, prefsOpen: false,
    openPreferences: () => {}, closePreferences: () => {},
    acceptAll: () => {}, rejectAll: () => {}, savePreferences: () => {},
    hasConsent: () => false,
  };
  return ctx;
}
